import { useEffect } from "react";
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import type { PluginListenerHandle } from "@capacitor/core";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@nfp-tracker/components";
import { captureException, trackEvent } from "../integrations/analytics/client";
import { supabase } from "../integrations/supabase/client";
import {
  mergeObservationRow,
  observationsQueryKey,
  removeObservationRow,
  type ObservationRow,
} from "./useObservationsCache";

type Props = {
  ownerUserId: string | undefined;
  actorUserId: string | undefined;
  hasSpouse: boolean;
};

const RETRY_DELAYS_MS = [1000, 2000, 5000, 10000, 30000];

/**
 * A websocket killed while the app was suspended does not report the failure until JS
 * resumes — by which point visibilityState/appState already read "foreground", so the
 * point-in-time backgrounded check below misses it. Everything inside this window after a
 * resume is treated as suspension fallout rather than a real subscribe failure.
 */
const RESUME_ERROR_GRACE_MS = 15000;

/**
 * Consecutive failed subscribe attempts before a failure is treated as real. A single
 * CHANNEL_ERROR is almost always a transient blip (wifi handoff, a proxy dropping the socket,
 * a token refresh landing mid-connect) that the very next retry recovers from — reporting the
 * first one buries the sustained outages that actually leave a user without live updates.
 */
const REALTIME_ERROR_REPORT_AFTER_RETRIES = 3;

/**
 * The single realtime subscription for the observations cache — mounted once in
 * AppShellLayout, not per-page. Merges changed rows directly into the react-query cache
 * instead of triggering a full page reload: this replaces the old per-page
 * useRealtimeObservations(onUpdate: fullReload) pattern, which was the source of a
 * save/revert race (a reload could clobber an in-progress local edit) and repeated-update
 * storms (every reload's own writes could trigger further reloads).
 *
 * Reconnects on CHANNEL_ERROR/TIMED_OUT with backoff, and forces a fresh subscribe when
 * the app returns to the foreground — mobile OSes suspend the websocket while backgrounded,
 * which otherwise leaves the channel dead (with no error) until the next full reload.
 */
export const useObservationsRealtimeSync = ({ ownerUserId, actorUserId, hasSpouse }: Props) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!ownerUserId) return;

    let channel: ReturnType<typeof supabase.channel> | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let retryCount = 0;
    let stopped = false;
    let appStateListener: PluginListenerHandle | null = null;
    let isNativeBackgrounded = false;
    // 0 (not mount time) so a genuine failure on a cold start is still reported.
    let lastResumedAt = 0;

    const clearRetryTimer = () => {
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
    };

    const connect = () => {
      channel = supabase
        .channel(`observations-cache-sync-${ownerUserId}`)
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "observations",
            filter: `owner_user_id=eq.${ownerUserId}`,
          },
          (payload) => {
            const key = observationsQueryKey(ownerUserId);

            queryClient.setQueryData<ObservationRow[]>(key, (prev) => {
              if (payload.eventType === "DELETE") {
                const deletedId = (payload.old as { id?: string } | undefined)?.id;
                return deletedId ? removeObservationRow(prev, deletedId) : prev;
              }

              return mergeObservationRow(prev, payload.new as ObservationRow);
            });

            if (payload.eventType === "INSERT" || payload.eventType === "UPDATE") {
              const changedBy = (payload.new as { created_by_user_id?: string } | undefined)
                ?.created_by_user_id;
              const isSelf = actorUserId && changedBy === actorUserId;

              if (hasSpouse && !isSelf) {
                toast({
                  title: "Data Updated",
                  description: "Your spouse just updated the data",
                });
              }
            }
          }
        )
        .subscribe((status, error) => {
          if (stopped) return;

          if (status === "SUBSCRIBED") {
            retryCount = 0;
            return;
          }

          if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
            // Backgrounding (mobile OS suspends the websocket) and offline tabs both
            // surface as CHANNEL_ERROR/TIMED_OUT and self-resolve on reconnect — not
            // real errors, so don't light up Sentry/PostHog for them.
            const isBackgroundedOrOffline =
              document.visibilityState !== "visible"
              || !navigator.onLine
              || isNativeBackgrounded;
            const isResumeFallout = Date.now() - lastResumedAt < RESUME_ERROR_GRACE_MS;
            // retryCount is only reset by a successful SUBSCRIBE, so this reports the attempt
            // that proves the channel is genuinely stuck rather than every transient drop.
            const isSustained = retryCount >= REALTIME_ERROR_REPORT_AFTER_RETRIES;
            if (!isBackgroundedOrOffline && !isResumeFallout && isSustained) {
              trackEvent("observations_realtime_sync_failed", { status, retryCount });
              captureException(error ?? new Error(`Realtime subscribe failed: ${status}`), {
                source: "observations_realtime_sync",
                retryCount,
              });
            }
            reconnect();
          }
        });
    };

    const reconnect = () => {
      if (stopped || retryTimer) return;

      const delay = RETRY_DELAYS_MS[Math.min(retryCount, RETRY_DELAYS_MS.length - 1)];
      retryCount += 1;

      retryTimer = setTimeout(() => {
        retryTimer = null;
        if (stopped) return;
        if (channel) supabase.removeChannel(channel);
        connect();
      }, delay);
    };

    const refreshAuthAndReconnect = async () => {
      // JS timers (and thus supabase-js's own auto-refresh) are suspended while the app
      // is backgrounded, so the session can hold a long-expired access token by the time
      // it resumes. Reconnecting with that stale token throws InvalidJWTToken and loops
      // on CHANNEL_ERROR — force a real refresh first.
      try {
        const { data: { session } } = await supabase.auth.refreshSession();
        if (session?.access_token) {
          supabase.realtime.setAuth(session.access_token);
        }
      } catch {
        // Best effort — connect() below still retries with backoff if the token is stale.
      }

      if (stopped) return;
      if (channel) supabase.removeChannel(channel);
      connect();
    };

    const handleVisibilityChange = () => {
      if (stopped || document.visibilityState !== "visible") return;
      lastResumedAt = Date.now();
      clearRetryTimer();
      retryCount = 0;
      void refreshAuthAndReconnect();
    };

    connect();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // On iOS the webview's visibilityState is not a reliable read of whether the OS
    // suspended us, so take the native app state as the source of truth when we have it.
    if (Capacitor.isNativePlatform()) {
      void CapacitorApp.addListener("appStateChange", ({ isActive }) => {
        isNativeBackgrounded = !isActive;
        if (isActive) {
          lastResumedAt = Date.now();
        }
      }).then((listener) => {
        if (stopped) {
          void listener.remove();
          return;
        }
        appStateListener = listener;
      });
    }

    return () => {
      stopped = true;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      void appStateListener?.remove();
      clearRetryTimer();
      if (channel) supabase.removeChannel(channel);
    };
  }, [ownerUserId, actorUserId, hasSpouse, queryClient]);
};
